import React, { useState } from "react";
import Header from "../../components/ui/Header";
import Icon from "../../components/AppIcon";

const API_BASE = import.meta.env.VITE_BACKEND_URL || "http://localhost:5001";

const WHY_ITEMS = [
  {
    icon: "Shield",
    title: "Experienced Sherpa Guides",
    desc: "Local experts who know every trail, weather pattern, and hidden gem — ensuring your safety and an authentic experience.",
  },
  {
    icon: "Heart",
    title: "Personalized & Flexible",
    desc: "No cookie-cutter trips. We tailor every detail to your fitness, schedule, and interests.",
  },
  {
    icon: "Users",
    title: "Community Support",
    desc: "Your booking directly supports Sherpa families, village schools, and trail maintenance.",
  },
  {
    icon: "CheckCircle",
    title: "100% Transparent",
    desc: "Licensed, insured, fair pricing — no hidden fees, ever.",
  },
  {
    icon: "RefreshCw",
    title: "Flexible Cancellation",
    desc: "Easy changes or refunds — because life happens and plans change.",
  },
  {
    icon: "Mountain",
    title: "Unforgettable Adventures",
    desc: "Epic views, rich culture, personal growth — trips you'll talk about for years.",
  },
];

const TESTIMONIALS = [
  {
    quote: "I have made several trek with Sherpatrekker! Five! Because I enjoyed so much the first one with Sherpatrekker that I would come again! Nepal is an enchanting country and all the trek I made with Sherpatrekker has been amazing! Pasang Sherpa the guide, is a true professional! His team too! Only good surprise with them! Discover new paths, meet the locals, their way of life and share special moments with them, always feel well supported, discover the flora and fauna, Sherpatrekker brings all that! Trek with Sherpatrekker is really a great experience full of unforgettable moments! I dream to go again trekking with Sherpatrekker and I thank very much Pasang Sherpa and his team for bringing me so much emotion during my holidays in Nepal!",
    author: "Odile Servy",
    location: "France",
    trek: "Multiple Treks with Sherpa Trekker",
  },
  {
    quote: "Thank you Sherpa Trekker for organizing this trip. It was a pleasure to meet you: Sanu, Pemba, Pasang's family, Fireman, Mama from the Garden Inn hotel, Maya, Khrishna and all the others. Thank you all for your smiles, your sharing, your joy, your welcome. We had a very nice trip among you. Thank you for all.",
    author: "Aurély & Family",
    location: "France",
    trek: "Langtang Trek",
  },
  {
    quote: "Pasang Sherpa is a very good trekking guide, who knows his business. Nothing to complain. I felt safe and well-protected with him during the entire trek. He has great knowledge of his country. He told me what to eat and what not and why. He showed me things that I would never seen without him. I am more than satisfied with his great service. Anytime again with him. It was very useful to get a big travel bag and map!",
    author: "Luigi",
    location: "Italy",
    trek: "Annapurna Circuit Trek",
  },
  {
    quote: "I booked three treks with 'Sherpatrekker': the first was the Ghorepani Poonhill trek, followed by a climb of Pikey Peak where I experienced the sunrise with a view of Everest, and finally a trek in the Langtang Valley. Along with his meticulous planning and sense of responsibility, the good humour of Pasang and his team ensured an unforgettable trek.",
    author: "Markus Balmer",
    location: "Switzerland",
    trek: "Ghorepani Poonhill · Pikey Peak · Langtang Valley",
  },
];

const COMMITMENTS = [
  "Leave No Trace — we pack out everything and follow eco-friendly practices on every trek.",
  "Support local communities — fair wages for porters & guides, plus contributions to schools and conservation.",
  "Cultural respect — we work only with licensed local experts and promote authentic Sherpa traditions.",
  "Low-impact groups — smaller group sizes to reduce strain on trails and villages.",
];

const BookingConsultationCenter = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", trek: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API_BASE}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.status === 429) {
        alert("You've sent a few messages already. Please wait a bit before sending another.");
        return;
      }
      if (!res.ok) throw new Error("Failed to send");
      setSubmitted(true);
      setCooldown(120);
      const timer = setInterval(() => {
        setCooldown((c) => {
          if (c <= 1) { clearInterval(timer); return 0; }
          return c - 1;
        });
      }, 1000);
    } catch (err) {
      alert("Something went wrong. Please email us directly at imsherpatrekker@gmail.com");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

        {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <section className="relative min-h-[50vh] sm:min-h-[60vh] flex items-end overflow-hidden">
        {/* Background image */}
        <img
          src="/assets/images/main-1.jpg"
          alt="Himalayan trek"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        {/* Overlays */}
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

        {/* Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 sm:pb-16 pt-28 sm:pt-40">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-[#c4873a] text-xs font-semibold tracking-[0.2em] uppercase mb-4">
              <span className="w-6 h-px bg-[#c4873a]" />
              Sherpa Trekker
            </span>
            <h1
              className="text-white mb-4 leading-tight"
              style={{
                fontFamily: "'Fraunces', serif",
                fontWeight: 300,
                fontSize: "clamp(2.5rem, 5vw, 4rem)",
              }}
            >
              Book Your<br />
              <span style={{ color: "#c4873a", fontStyle: "italic" }}>Himalayan Adventure</span>
            </h1>
            <p className="text-white/80 text-lg leading-relaxed max-w-xl">
              Get in touch with our expert team to plan your perfect trek —
              personalized advice, no pressure, just genuine help.
            </p>
          </div>
        </div>
      </section>

      {/* ── Main ─────────────────────────────────────────────────────────────── */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-20">

        {/* ── Contact + Form ──────────────────────────────────────────────────── */}
        <section className="grid lg:grid-cols-2 gap-12 items-start">

          {/* Left — contact info */}
          <div>
            <h2
              className="text-foreground mb-3"
              style={{ fontFamily: "'Fraunces',serif", fontWeight: 300, fontSize: "2rem" }}
            >
              Get in <span style={{ color: "#6b7f4a" }}>Touch</span>
            </h2>
            <p className="text-text-secondary mb-8 leading-relaxed">
              Our trek experts are ready to answer your questions about routes,
              dates, difficulty, preparation — or anything else on your mind.
            </p>

            {/* Contact cards */}
            <div className="space-y-4 mb-10">
              <a
                href="tel:+9779851157213"
                className="flex items-center gap-4 p-5 bg-card border border-border rounded-2xl hover:border-primary/40 hover:shadow-md transition-all duration-200 group"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                  <Icon name="Phone" size={22} className="text-primary" />
                </div>
                <div>
                  <p className="text-xs text-text-secondary uppercase tracking-wide mb-0.5">Call us</p>
                  <p className="font-semibold text-foreground">+977-9851157213</p>
                  <p className="text-xs text-text-secondary mt-0.5">Available 7 days a week</p>
                </div>
                <Icon name="ArrowRight" size={16} className="text-text-secondary ml-auto group-hover:text-primary group-hover:translate-x-1 transition-all" />
              </a>

              <a
                href="mailto:imsherpatrekker@gmail.com"
                className="flex items-center gap-4 p-5 bg-card border border-border rounded-2xl hover:border-primary/40 hover:shadow-md transition-all duration-200 group"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                  <Icon name="Mail" size={22} className="text-primary" />
                </div>
                <div>
                  <p className="text-xs text-text-secondary uppercase tracking-wide mb-0.5">Email us</p>
                  <p className="font-semibold text-foreground">imsherpatrekker@gmail.com</p>
                  <p className="text-xs text-text-secondary mt-0.5">Response within 24 hours</p>
                </div>
                <Icon name="ArrowRight" size={16} className="text-text-secondary ml-auto group-hover:text-primary group-hover:translate-x-1 transition-all" />
              </a>

              <div className="flex items-center gap-4 p-5 bg-card border border-border rounded-2xl">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="MapPin" size={22} className="text-primary" />
                </div>
                <div>
                  <p className="text-xs text-text-secondary uppercase tracking-wide mb-0.5">Visit us</p>
                  <p className="font-semibold text-foreground">Thamel, Kathmandu, Nepal</p>
                  <p className="text-xs text-text-secondary mt-0.5">Open Mon–Sat, 9am–6pm</p>
                </div>
              </div>
            </div>

            {/* Quick stats */}
            <div className="grid grid-cols-3 gap-4">
              {[
                { value: "15+", label: "Trek Routes" },
                { value: "98%", label: "Success Rate" },
                { value: "100+", label: "Happy Trekkers" },
              ].map((s, i) => (
                <div key={i} className="bg-primary/5 rounded-xl p-4 text-center border border-primary/10">
                  <p
                    className="text-2xl font-bold text-primary"
                    style={{ fontFamily: "'Fraunces',serif" }}
                  >
                    {s.value}
                  </p>
                  <p className="text-xs text-text-secondary mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — enquiry form */}
          <div className="bg-card border border-border rounded-2xl p-8 shadow-sm">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 mx-auto mb-6 relative">
                  <div className="absolute inset-0 bg-primary/10 rounded-full animate-ping opacity-30" />
                  <div className="relative w-full h-full bg-gradient-to-br from-primary to-himalayan-blue rounded-full flex items-center justify-center shadow-lg">
                    <Icon name="Mountain" size={36} className="text-white" />
                  </div>
                </div>
                <h3 className="font-headline font-bold text-2xl text-foreground mb-2">Namaste!</h3>
                <p className="text-text-secondary text-lg mb-2">Your message has been received.</p>
                <p className="text-text-secondary text-sm mb-6">
                  We'll be in touch within 24 hours — just like the Himalayas, great adventures are worth the wait.
                </p>
                {cooldown > 0 ? (
                  <p className="text-xs text-text-secondary">
                    You can send another message in{" "}
                    <span className="font-semibold text-primary">{Math.floor(cooldown / 60)}:{(cooldown % 60).toString().padStart(2, "0")}</span>
                  </p>
                ) : (
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-2 text-sm text-primary hover:underline font-medium"
                  >
                    Send another message
                  </button>
                )}
              </div>
            ) : (
              <>
                <h3
                  className="text-foreground mb-6"
                  style={{ fontFamily: "'Fraunces',serif", fontWeight: 300, fontSize: "1.5rem" }}
                >
                  Send an Enquiry
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">Full Name *</label>
                      <input
                        name="name"
                        required
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">Email *</label>
                      <input
                        name="email"
                        type="email"
                        required
                        value={form.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors text-sm"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">Phone</label>
                      <input
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+1 234 567 8900"
                        className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">Trek of Interest</label>
                      <select
                        name="trek"
                        value={form.trek}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors text-sm"
                      >
                        <option value="">Select a trek…</option>
                        <option>Everest Base Camp</option>
                        <option>Annapurna Base Camp</option>
                        <option>Annapurna Circuit</option>
                        <option>Manaslu Circuit</option>
                        <option>Langtang Valley</option>
                        <option>Ghorepani Poon Hill</option>
                        <option>Upper Mustang</option>
                        <option>Nar Phu Valley</option>
                        <option>Khopra Danda</option>
                        <option>Other / Not sure yet</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Message</label>
                    <textarea
                      name="message"
                      rows={4}
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Tell us about your travel dates, group size, experience level, or any questions…"
                      className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors text-sm resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary text-white font-semibold rounded-xl hover:bg-primary/90 transition-all duration-200 hover:-translate-y-0.5 shadow-sm hover:shadow-md text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <Icon name="Send" size={16} />
                    {sending ? "Sending..." : "Send Enquiry"}
                  </button>
                  <p className="text-center text-xs text-text-secondary">
                    We respond within 24 hours · No spam, ever
                  </p>
                </form>
              </>
            )}
          </div>
        </section>

        {/* ── Why Book With Us ────────────────────────────────────────────────── */}
        <section>
          <div className="text-center mb-12">
            <h2
              className="text-foreground mb-3"
              style={{ fontFamily: "'Fraunces',serif", fontWeight: 300, fontSize: "2rem" }}
            >
              Why Book With{" "}
              <span style={{ color: "#2d5016" }}>Sherpa Trekker</span>?
            </h2>
            <p className="text-text-secondary max-w-xl mx-auto">
              We're not just a trekking company — we're your partners in the mountains.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_ITEMS.map((item, i) => (
              <div key={i} className="bg-card border border-border rounded-2xl p-6 hover:shadow-md hover:border-primary/30 transition-all duration-200">
                <div className="w-11 h-11 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                  <Icon name={item.icon} size={22} className="text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">{item.title}</h3>
                <p className="text-sm text-text-secondary leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Testimonials ────────────────────────────────────────────────────── */}
        <section>
          <div className="text-center mb-12">
            <h2
              className="text-foreground mb-3"
              style={{ fontFamily: "'Fraunces',serif", fontWeight: 300, fontSize: "2rem" }}
            >
              What <span style={{ color: "#c4873a" }}>Our Trekkers</span> Say
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-card border border-border rounded-2xl p-6 flex flex-col gap-4">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, j) => (
                    <Icon key={j} name="Star" size={15} className="text-yellow-400" />
                  ))}
                </div>
                <p className="text-text-secondary italic leading-relaxed flex-1">"{t.quote}"</p>
                <div className="border-t border-border pt-4">
                  <p className="font-semibold text-foreground text-sm">{t.author}</p>
                  <p className="text-xs text-text-secondary">{t.location} · {t.trek}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Our Commitment ──────────────────────────────────────────────────── */}
        <section className="bg-primary/5 border border-primary/15 rounded-2xl p-8 lg:p-12">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
            <h2
              className="text-foreground mb-4"
              style={{ fontFamily: "'Fraunces',serif", fontWeight: 300, fontSize: "2rem" }}
            >
              Our <span style={{ color: "#2d5016" }}>Commitment</span> to You
            </h2>
              <p className="text-text-secondary mb-6 leading-relaxed">
                We are deeply committed to <strong>responsible</strong> and{" "}
                <strong>sustainable tourism</strong> in the Himalayas. Your adventure
                should leave the mountains better than we found them.
              </p>
              <ul className="space-y-3">
                {COMMITMENTS.map((c, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-text-secondary">
                    <div className="w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon name="Check" size={12} className="text-primary" />
                    </div>
                    {c}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative rounded-2xl overflow-hidden" style={{ minHeight: "280px" }}>
              <img
                src="/assets/images/carousel.jpeg"
                alt="Sherpa guides in the mountains"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <div className="absolute bottom-5 left-5 text-white">
                <p className="text-sm font-semibold">Guided by generations</p>
                <p className="text-xs text-white/70">of Sherpa mountain wisdom</p>
              </div>
            </div>
          </div>
        </section>

        {/* ── CTA strip ───────────────────────────────────────────────────────── */}
        <section className="relative overflow-hidden rounded-2xl">
          <img
            src="/assets/images/everest.jpg"
            alt="Himalayan peaks"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/70 via-white/50 to-white/30" />
          <div className="relative text-center text-white px-6 sm:px-8 py-12 sm:py-16">
            <span className="inline-flex items-center gap-2 text-black/50 text-xs font-semibold tracking-[0.2em] uppercase mb-4">
              <span className="w-5 h-px bg-black/30" />
              Let's Talk
              <span className="w-5 h-px bg-black/30" />
            </span>
            <h2
              className="mb-4 drop-shadow-lg"
              style={{ fontFamily: "'Fraunces',serif", fontWeight: 300, fontSize: "clamp(1.8rem,4vw,2.8rem)", color: "#1c1c1a" }}
            >
              Ready to Start Your{" "}
              <span style={{ color: "#c4873a" }}>Journey</span>?
            </h2>
            <p className="text-black/75 mb-10 max-w-lg mx-auto leading-relaxed drop-shadow">
              Call us or send an email — our team responds within 24 hours.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <a
                href="tel:+9779851157213"
                className="flex items-center gap-2 px-6 py-2.5 bg-primary text-white font-semibold rounded-lg hover:bg-primary/90 transition-all duration-200 hover:-translate-y-0.5 shadow-md text-sm"
              >
                <Icon name="Phone" size={15} />
                Call Us
              </a>
              <a
                href="mailto:imsherpatrekker@gmail.com"
                className="flex items-center gap-2 px-6 py-2.5 bg-white/80 backdrop-blur-sm border border-black/20 text-primary font-semibold rounded-lg hover:bg-white transition-all duration-200 hover:-translate-y-0.5 text-sm"
              >
                <Icon name="Mail" size={15} />
                Email Us
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-foreground text-white py-10 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-white/60">
            © {new Date().getFullYear()} Sherpa Trekker. All rights reserved. · Licensed Trek Operator in Nepal
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BookingConsultationCenter;
